import type { Option } from '../components/types';

/**
 * Pure selection model shared by `au-multiple-search-select` and
 * `au-async-multiple-search-select`. `value` (the ids) is the only truth of what
 * is selected; labels are a memory that never forgets an id it has seen, so a
 * change of `options` (a search, a page) can never drop a selected id.
 */

/** id → option, learned from `selected`, and from options whose id is in `value`. */
export function mergeOptionLabels(
  previous: ReadonlyMap<string, Option>,
  selected: readonly Option[],
  options: readonly Option[],
  value: readonly string[],
): Map<string, Option> {
  const memory = new Map(previous);
  for (const option of selected) memory.set(option.value, option);
  const ids = new Set(value);
  for (const option of options) {
    if (ids.has(option.value)) memory.set(option.value, option);
  }
  return memory;
}

/** One row per id, in `value` order. An unknown label falls back to the raw id: a visible failure. */
export function resolveSelectedRows(
  value: readonly string[],
  memory: ReadonlyMap<string, Option>,
): Option[] {
  return value.map((id) => memory.get(id) ?? { value: id, label: id });
}

/** Adds or removes only `id`; every other id is kept, even when it is not in the current options. */
export function toggleSelectedId(value: readonly string[], id: string): string[] {
  return value.includes(id) ? value.filter((current) => current !== id) : [...value, id];
}

/** The options that are not selected, filtered by a case-insensitive substring of the label. */
export function unselectedMatching<T extends Option>(
  options: readonly T[],
  value: readonly string[],
  search: string,
): T[] {
  const ids = new Set(value);
  const needle = search.toLowerCase();
  return options.filter(
    (option) => !ids.has(option.value) && option.label.toLowerCase().includes(needle),
  );
}

export interface HighlightableRow {
  value: string;
  disabled: boolean;
}

/** Where the previous active row sat: its list (selected group or main list) and its position in it. */
export interface HighlightPosition {
  list: 'selected' | 'main';
  index: number;
}

/**
 * Index (in the rendered rows: selected group first, then the main list) of the row the command
 * must highlight, or -1. An eligible row is enabled and, while searching, not selected. With no
 * `position` it is the first eligible row; with one, the eligible row nearest to that position in
 * the SAME list (forward first, then backward). If that list has none, it falls back to the main
 * list from its start, then to the first eligible row overall.
 */
export function pickHighlightIndex(
  rows: readonly HighlightableRow[],
  selectedIds: readonly string[],
  searching: boolean,
  position: HighlightPosition | null = null,
): number {
  const ids = new Set(selectedIds);
  const eligible = (row: HighlightableRow) => !row.disabled && !(searching && ids.has(row.value));
  const firstEligible = rows.findIndex(eligible);
  if (position === null) return firstEligible;

  const indexesOf = (selected: boolean) =>
    rows.flatMap((row, index) => (ids.has(row.value) === selected ? [index] : []));
  const nearest = (list: number[], from: number) => {
    const start = Math.min(Math.max(from, 0), list.length - 1);
    return (
      list.slice(start).find((index) => eligible(rows[index])) ??
      list
        .slice(0, Math.max(start, 0))
        .reverse()
        .find((index) => eligible(rows[index]))
    );
  };

  const sameList = nearest(indexesOf(position.list === 'selected'), position.index);
  if (sameList !== undefined) return sameList;
  return nearest(indexesOf(false), 0) ?? firstEligible;
}
